import { Injectable } from '@jkhy/frontend-utils/di';
import { ObservationSource } from '@jkhy/frontend-utils/observable';
import { FiPermissionModelDto } from '@treasury/api/bo';
import { EntitlementsClient as BoEntitlementsClient } from '@treasury/api/bo/generated/Entitlements';
import {
    EntitlementsClient as ChannelEntitlementsClient,
    ProductPermissionModelDto,
} from '@treasury/api/channel';
import { AppType, ConfigurationService } from '@treasury/core/config';
import { TmHttpClient } from '@treasury/core/http';
import { EntitlementDto } from '../services/feature-flags';
import { Entitlements } from './entitlements.type';

@Injectable()
export class EntitlementsService {
    constructor(
        private readonly http: TmHttpClient,
        private readonly configService: ConfigurationService,
        private readonly channelClient: ChannelEntitlementsClient,
        private readonly boClient: BoEntitlementsClient
    ) {}

    private initialized = false;

    private readonly readyStream = new ObservationSource<boolean>();

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly ready$ = this.readyStream.toObservable();

    private entitlementsMap = new Map<string, boolean>();

    public async init() {
        if (this.ready$.completed || this.initialized) {
            return;
        }

        try {
            const opts = { maxAgeInSeconds: 60 };
            const response =
                this.configService.app === AppType.BackOffice
                    ? await this.getBackofficeEntitlements(opts)
                    : await this.channelClient.entitlementsGet(opts);
            this.hydrateMap(response.data);
            this.setCapabilities();
        } catch (e) {
            this.readyStream.setError(new Error('Could not initialize entitlement service.'));
            this.readyStream.complete();
            throw e;
        }

        this.initialized = true;
        this.readyStream.emit(true);
    }

    public reset() {
        this.entitlementsMap.clear();
        this.initialized = false;
        this.readyStream.emit(false);
    }

    // for backwards compatibility
    public async getEntitlements() {
        await this.ready$.toPromise();
        return this.entitlementsMap;
    }

    public async isEnabled(...entitlements: Entitlements[]) {
        await this.ready$.toPromise();
        return entitlements.every(e => this.entitlementsMap.get(e));
    }

    public async isAnyEnabled(...entitlements: Entitlements[]) {
        await this.ready$.toPromise();
        return entitlements.some(e => this.entitlementsMap.get(e));
    }

    private async getBackofficeEntitlements(opts?: {
        maxAgeInSeconds: number;
    }): Promise<{ data: FiPermissionModelDto[] }> {
        return this.boClient.entitlementsGet({
            maxAgeInSeconds: opts?.maxAgeInSeconds || 50000,
        });
    }

    private hydrateMap(
        entitlements: ProductPermissionModelDto[] | FiPermissionModelDto[] | EntitlementDto[]
    ) {
        entitlements.forEach(e => {
            const name =
                (e as ProductPermissionModelDto).permission || (e as FiPermissionModelDto).name;
            if (name && name.length > 0) {
                this.entitlementsMap.set(name, true);
            }
        });
    }

    private async setCapabilities() {
        if (this.configService.app === AppType.BackOffice) {
            return;
        }

        const capabilities = [
            {
                name: Entitlements.achEnabled,
                entitlements: [
                    Entitlements.RestrictAchBatch,
                    Entitlements.WorkAchExceptions,
                    Entitlements.CreateAchTemplate,
                    Entitlements.FullEditAchTemplate,
                    Entitlements.PartialEditAchTemplate,
                    Entitlements.DeleteAchTemplate,
                    Entitlements.ApproveAchTemplate,
                    Entitlements.CreateAchPayment,
                    Entitlements.FullEditAchPayment,
                    Entitlements.PartialEditAchPayment,
                    Entitlements.DeleteAchPayment,
                    Entitlements.ApproveAchPayment,
                    Entitlements.CreateInternationalAchPayment,
                    Entitlements.FullEditInternationalAchPayment,
                    Entitlements.PartialEditInternationalAchPayment,
                    Entitlements.DeleteInternationalAchPayment,
                    Entitlements.ApproveInternationalAchPayment,
                    Entitlements.FileUploadEditInternationalAchPayment,
                ],
            },
            {
                name: Entitlements.transfersEnabled,
                entitlements: [
                    Entitlements.CreateTransfer,
                    Entitlements.EditTransfer,
                    Entitlements.DeleteTransfer,
                    Entitlements.ApproveTransfer,
                ],
            },
            {
                name: Entitlements.wiresEnabled,
                entitlements: [
                    Entitlements.CreateDomesticWire,
                    Entitlements.CreateInternationalWire,
                    Entitlements.EditDomesticWirePayment,
                    Entitlements.EditInternationalWirePayment,
                    Entitlements.DeleteDomesticWirePayment,
                    Entitlements.DeleteInternationalWirePayment,
                    Entitlements.ApproveInternationalWirePayment,
                    Entitlements.RejectWire,
                    Entitlements.WireEditBeneficiary,
                    Entitlements.WireEditBeneficiary,
                    Entitlements.WireApproveBeneficiary,
                ],
            },
            { name: Entitlements.billPayEnabled, entitlements: [Entitlements.BusinessBillPay] },
            {
                name: Entitlements.remoteDepositsEnabled,
                entitlements: [Entitlements.RemoteDeposits],
            },
        ];

        for (const capability of capabilities) {
            if (await this.isAnyEnabled(...capability.entitlements)) {
                this.entitlementsMap.set(capability.name, true);
            }
        }
    }
}
